import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission, Role } from '../../services/permissions/permissions.service';
import { resolveModules } from '../../core/business-profile/businessProfileResolver';
import type { BusinessProfile } from '../../core/business-profile/businessProfile';

export interface RouteGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export interface PermissionGuardProps extends RouteGuardProps {
  permission?: Permission;
  permissions?: Permission[];
  requireAll?: boolean;
  role?: Role;
  roles?: Role[];
}

/**
 * AuthGuard - Protege rotas autenticadas
 * Se não autenticado, redireciona para login
 */
export function AuthGuard({ children, fallback }: RouteGuardProps) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#111111]/20 border-t-[#111111] rounded-full animate-spin" />
          <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-widest">
            Carregando...
          </span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      fallback || (
        <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#111111]">Não Autenticado</h1>
            <p className="text-[#64748B] mt-2">Você precisa fazer login para acessar esta página.</p>
          </div>
        </div>
      )
    );
  }

  return <>{children}</>;
}

/**
 * OnboardingGuard - Verifica se onboarding foi completado
 */
export function OnboardingGuard({ children, fallback }: RouteGuardProps) {
  const { activeWorkspace, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#111111]/20 border-t-[#111111] rounded-full animate-spin" />
          <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-widest">
            Carregando...
          </span>
        </div>
      </div>
    );
  }

  const isOnboardingComplete = activeWorkspace?.settings?.onboardingCompleted === true;

  if (!isOnboardingComplete) {
    return (
      fallback || (
        <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#111111]">Onboarding Necessário</h1>
            <p className="text-[#64748B] mt-2">Por favor, complete o onboarding antes de continuar.</p>
          </div>
        </div>
      )
    );
  }

  return <>{children}</>;
}

/**
 * WorkspaceGuard - Verifica se um workspace está selecionado
 */
export function WorkspaceGuard({ children, fallback }: RouteGuardProps) {
  const { activeWorkspace, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#111111]/20 border-t-[#111111] rounded-full animate-spin" />
          <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-widest">
            Carregando...
          </span>
        </div>
      </div>
    );
  }

  if (!activeWorkspace) {
    return (
      fallback || (
        <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#111111]">Nenhum Workspace</h1>
            <p className="text-[#64748B] mt-2">Você precisa selecionar um workspace para continuar.</p>
          </div>
        </div>
      )
    );
  }

  return <>{children}</>;
}

/**
 * FullAccessGuard - Verifica autenticação, workspace e onboarding
 */
export function FullAccessGuard({ children, fallback }: RouteGuardProps) {
  const { isAuthenticated, activeWorkspace, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#111111]/20 border-t-[#111111] rounded-full animate-spin" />
          <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-widest">
            Carregando...
          </span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      fallback || (
        <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#111111]">Não Autenticado</h1>
            <p className="text-[#64748B] mt-2">Você precisa fazer login.</p>
          </div>
        </div>
      )
    );
  }

  if (!activeWorkspace) {
    return (
      fallback || (
        <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#111111]">Nenhum Workspace</h1>
            <p className="text-[#64748B] mt-2">Selecione um workspace.</p>
          </div>
        </div>
      )
    );
  }

  const isOnboardingComplete = activeWorkspace?.settings?.onboardingCompleted === true;

  if (!isOnboardingComplete) {
    return (
      fallback || (
        <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#111111]">Onboarding Necessário</h1>
            <p className="text-[#64748B] mt-2">Complete o onboarding para continuar.</p>
          </div>
        </div>
      )
    );
  }

  return <>{children}</>;
}

/**
 * PermissionGuard — verifica se o usuário tem permissão para acessar a rota.
 *
 * Usage:
 *   <PermissionGuard permission="workspace.manage_members">
 *     <MembersSettings />
 *   </PermissionGuard>
 *
 *   <PermissionGuard permissions={['workspace.manage_settings', 'admin.roles']}>
 *     <SettingsPanel />
 *   </PermissionGuard>
 */
export function PermissionGuard({
  children,
  fallback,
  permission,
  permissions,
  requireAll = false,
  role,
  roles,
}: PermissionGuardProps) {
  const { can, canAny, canAll, hasAnyRole } = usePermissions();

  let hasAccess = true;

  if (permissions && permissions.length > 0) {
    hasAccess = requireAll
      ? canAll(permissions)
      : canAny(permissions);
  }

  if (hasAccess && permission) {
    hasAccess = can(permission);
  }

  if (hasAccess && role) {
    hasAccess = hasAnyRole([role]);
  }

  if (hasAccess && roles) {
    hasAccess = hasAnyRole(roles);
  }

  if (!hasAccess) {
    return (
      fallback || (
        <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#111111]">Acesso Restrito</h1>
            <p className="text-[#64748B] mt-2">Você não possui permissão para acessar esta página.</p>
          </div>
        </div>
      )
    );
  }

  return <>{children}</>;
}

/**
 * RoleGuard — verifica se o usuário tem um role ou roles específicos.
 */
export function RoleGuard({ children, fallback, roles }: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  roles: Role[];
}) {
  return (
    <PermissionGuard roles={roles} fallback={fallback}>
      {children}
    </PermissionGuard>
  );
}

/**
 * AppAccessGuard — verifica se o app está ativo no workspace e se o usuário tem acesso
 * 
 * Usage:
 *   <AppAccessGuard appId="crm">
 *     <CRMModule />
 *   </AppAccessGuard>
 */
export function AppAccessGuard({
  children,
  fallback,
  appId,
}: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  appId: string;
}) {
  const { activeWorkspace, loading } = useAuth();
  const { can, isInitialized } = usePermissions();
  
  if (loading || !isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#111111]/20 border-t-[#111111] rounded-full animate-spin" />
          <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-widest">
            Carregando...
          </span>
        </div>
      </div>
    );
  }

  if (!activeWorkspace) {
    return (
      fallback || (
        <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#111111]">Workspace Inválido</h1>
            <p className="text-[#64748B] mt-2">Nenhum workspace selecionado.</p>
          </div>
        </div>
      )
    );
  }

  // Get enabled apps from workspace settings
  const profile = activeWorkspace.settings?.businessProfile as BusinessProfile | undefined;
  const enabledApps = profile
    ? resolveModules(profile)
    : (activeWorkspace.settings?.enabledApps as string[]) || [];
  const appIsEnabled = enabledApps.includes(appId);

  if (!appIsEnabled) {
    return (
      fallback || (
        <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#111111]">App Não Disponível</h1>
            <p className="text-[#64748B] mt-2">
              Este aplicativo não está ativo neste workspace.
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="mt-4 px-4 py-2 bg-[#111111] text-white rounded-lg text-sm font-medium hover:bg-[#1a1a1a]"
            >
              Voltar para o Home
            </button>
          </div>
        </div>
      )
    );
  }

  return <>{children}</>;
}
